/**
 * @ken/module-forms — the Forms screens (PRD 48 §7). Only the route table is
 * eager here; every screen downloads the first time it is opened (ADR-040,
 * BND-01). Two screens have no cockpit shell and are mounted by the cockpit's
 * own file routes: the print sheet ($tenantSlug_.forms.$id.print.tsx) and the
 * driver's phone page (f.$link.tsx).
 */

import { lazyScreen, type AnyRoute, type CreateModuleRoutes } from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound } from "@tanstack/react-router";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadFilled = () => import("./routes/filled");

const FormsListPage = lazyScreen(() => import("./routes/list"), "FormsListPage");
const FormsNewPage = lazyScreen(() => import("./routes/new"), "FormsNewPage");
const FormRecordPage = lazyScreen(() => import("./routes/record"), "FormRecordPage");
const FormFillPage = lazyScreen(() => import("./routes/fill"), "FormFillPage");
const FormPreviewPage = lazyScreen(() => import("./routes/preview"), "FormPreviewPage");
const FilledListPage = lazyScreen(loadFilled, "FilledListPage");
const FilledCopyPage = lazyScreen(loadFilled, "FilledCopyPage");

/** The print sheet, mounted shell-less by the cockpit. */
export const FormsPrintScreen = lazyScreen(() => import("./routes/print"), "PrintScreen");
/** The driver's phone page, mounted shell-less (no login) by the cockpit. */
export const FormsPhoneScreen = lazyScreen(() => import("./routes/phone"), "PhoneTicketPage");

/** The pages Secure links' /l/ page shows for Forms' link kinds (manifest frontend.link_pages). */
export const linkPages = {
  FormFillLinkPage: lazyScreen(() => import("./routes/link"), "FormFillLinkPage"),
};

export interface FormPreviewSearch {
  which?: "draft" | "published";
}

export interface FormsListSearch {
  state?: "published" | "drafts" | "archived";
}

export interface FilledListSearch {
  status?: "waiting";
  form_id?: string;
}

function idGuard(key: string) {
  return ({ params }: { params: unknown }): void => {
    if (!isRecordId((params as Record<string, string | undefined>)[key])) throw notFound();
  };
}

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const list = createRoute({
    getParentRoute: () => parentRoute,
    path: "forms",
    validateSearch: (raw: Record<string, unknown>): FormsListSearch => {
      const s = raw["state"];
      return s === "drafts" || s === "archived" || s === "published" ? { state: s } : {};
    },
    component: FormsListPage,
  });
  const create = createRoute({ getParentRoute: () => parentRoute, path: "forms/new", component: FormsNewPage });
  const copies = createRoute({
    getParentRoute: () => parentRoute,
    path: "forms/filled",
    validateSearch: (raw: Record<string, unknown>): FilledListSearch => ({
      ...(raw["status"] === "waiting" ? { status: "waiting" as const } : {}),
      ...(typeof raw["form_id"] === "string" && raw["form_id"] ? { form_id: raw["form_id"] } : {}),
    }),
    component: FilledListPage,
  });
  const copy = createRoute({
    getParentRoute: () => parentRoute,
    path: "forms/filled/$copyId",
    beforeLoad: idGuard("copyId"),
    notFoundComponent: RecordNotFound,
    component: FilledCopyPage,
  });
  const fill = createRoute({
    getParentRoute: () => parentRoute,
    path: "forms/$formId/fill",
    beforeLoad: idGuard("formId"),
    notFoundComponent: RecordNotFound,
    component: FormFillPage,
  });
  const preview = createRoute({
    getParentRoute: () => parentRoute,
    path: "forms/$formId/preview",
    beforeLoad: idGuard("formId"),
    validateSearch: (raw: Record<string, unknown>): FormPreviewSearch =>
      raw["which"] === "draft" || raw["which"] === "published" ? { which: raw["which"] } : {},
    notFoundComponent: RecordNotFound,
    component: FormPreviewPage,
  });
  const record = createRoute({
    getParentRoute: () => parentRoute,
    path: "forms/$formId",
    beforeLoad: idGuard("formId"),
    notFoundComponent: RecordNotFound,
    component: FormRecordPage,
  });
  // Static before parameterised.
  return [list, create, copies, copy, fill, preview, record];
};
